import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../utils/api";

const TYPE_COLORS = {
  sars:    { bg: "bg-red-50    border-red-200",    dot: "bg-red-500",     badge: "bg-red-100 text-red-700"   },
  vat:     { bg: "bg-purple-50 border-purple-200", dot: "bg-purple-500",  badge: "bg-purple-100 text-purple-700" },
  paye:    { bg: "bg-amber-50  border-amber-200",  dot: "bg-amber-500",   badge: "bg-amber-100 text-amber-700" },
  custom:  { bg: "bg-blue-50   border-blue-200",   dot: "bg-blue-500",    badge: "bg-blue-100 text-blue-700" },
  milestone: { bg: "bg-green-50 border-green-200", dot: "bg-green-500",   badge: "bg-green-100 text-green-700" },
  filing:  { bg: "bg-teal-50   border-teal-200",   dot: "bg-teal-500",    badge: "bg-teal-100 text-teal-700" },
  payment: { bg: "bg-orange-50 border-orange-200", dot: "bg-orange-500",  badge: "bg-orange-100 text-orange-700" },
  deadline:{ bg: "bg-red-50    border-red-200",    dot: "bg-red-500",     badge: "bg-red-100 text-red-700" },
};

function colorFor(type) {
  return TYPE_COLORS[type] || TYPE_COLORS.custom;
}

function daysUntil(dateStr) {
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "2-digit", month: "long", year: "numeric" });
}

export default function TaxCalendar() {
  const { token } = useAuth();
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: "", description: "", event_date: "", event_type: "custom" });

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/calendar/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const toggleComplete = async (event) => {
    try {
      const res = await fetch(`${API_BASE}/api/calendar/${event.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ is_completed: !event.is_completed }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === event.id ? updated : e));
      }
    } catch (err) { console.error(err); }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/calendar/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(newEvent),
      });
      if (res.ok) {
        const created = await res.json();
        setEvents(prev => [...prev, created].sort((a, b) => a.event_date.localeCompare(b.event_date)));
        setNewEvent({ title: "", description: "", event_date: "", event_type: "custom" });
        setShowAdd(false);
      }
    } catch (err) { console.error(err); }
  };

  const deleteEvent = async (id) => {
    await fetch(`${API_BASE}/api/calendar/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setEvents(prev => prev.filter(e => e.id !== id));
  };

  const filtered = filter === "upcoming"
    ? events.filter(e => !e.is_completed && daysUntil(e.event_date) >= 0)
    : filter === "completed"
    ? events.filter(e =>  e.is_completed)
    : filter === "sars"
    ? events.filter(e =>  e.is_sars_official)
    : events;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <svg className="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>
  );

  const upcoming = events.filter(e => !e.is_completed && daysUntil(e.event_date) >= 0 && daysUntil(e.event_date) <= 30);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tax Calendar</h1>
            <p className="text-sm text-gray-500 mt-0.5">South African SARS deadlines & your custom tax events</p>
          </div>
          <button onClick={() => setShowAdd(true)}
            className="px-4 py-2.5 bg-blue-700 text-white text-sm font-bold rounded-xl hover:bg-blue-800 transition-colors shadow-sm">
            + Add Event
          </button>
        </div>

        {/* Upcoming Alert */}
        {upcoming.length > 0 && (
          <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-2xl flex gap-3 items-start">
            <span className="text-2xl">⚠️</span>
            <div>
              <h4 className="font-bold text-red-800 text-sm">Upcoming Deadlines (Next 30 days)</h4>
              <div className="mt-1 space-y-1">
                {upcoming.slice(0, 3).map(e => (
                  <p key={e.id} className="text-red-700 text-sm">
                    <strong>{e.title}</strong> — {formatDate(e.event_date)}
                    <span className="ml-2 text-xs font-bold">({daysUntil(e.event_date)} days)</span>
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-5 flex-wrap">
          {[
            { key: "all",       label: "All Events" },
            { key: "upcoming",  label: "Upcoming" },
            { key: "sars",      label: "SARS Official" },
            { key: "completed", label: "Completed" },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors
                ${filter === f.key ? "bg-blue-700 text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Events List */}
        {filtered.length === 0 ? (
          <div className="text-center py-14 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-3">📅</div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No events found</h3>
            <p className="text-gray-500 text-sm">Add a custom event or switch filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(event => {
              const c   = colorFor(event.event_type);
              const d   = daysUntil(event.event_date);
              const past = d < 0;
              return (
                <div key={event.id}
                  className={`flex gap-4 p-4 rounded-2xl border transition-all
                    ${event.is_completed ? "bg-white border-gray-100 opacity-60" : c.bg}`}>
                  {/* Left dot + date */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0 w-16 text-center">
                    <div className={`w-3 h-3 rounded-full ${event.is_completed ? "bg-gray-300" : c.dot}`} />
                    <span className="text-xs font-bold text-gray-500">
                      {new Date(event.event_date).toLocaleDateString("en-ZA", { day: "2-digit", month: "short" })}
                    </span>
                    {!event.is_completed && !past && d <= 30 && (
                      <span className="text-xs font-bold text-red-600">{d}d</span>
                    )}
                    {past && !event.is_completed && (
                      <span className="text-xs font-bold text-gray-400">Past</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className={`font-bold text-sm ${event.is_completed ? "line-through text-gray-400" : "text-gray-900"}`}>
                        {event.title}
                        {event.is_sars_official && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-semibold">SARS</span>
                        )}
                      </h4>
                      <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${c.badge}`}>
                        {event.event_type}
                      </span>
                    </div>
                    {event.description && (
                      <p className="text-sm text-gray-500 mt-1">{event.description}</p>
                    )}
                    <div className="flex gap-3 mt-3">
                      <button onClick={() => toggleComplete(event)}
                        className={`text-xs font-semibold transition-colors
                          ${event.is_completed ? "text-blue-600 hover:underline" : "text-green-600 hover:underline"}`}>
                        {event.is_completed ? "Mark incomplete" : "Mark complete"}
                      </button>
                      {!event.is_sars_official && (
                        <button onClick={() => deleteEvent(event.id)} className="text-xs font-semibold text-red-500 hover:underline">
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Event Modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">Add Custom Event</h2>
              <form onSubmit={addEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input required value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})}
                    placeholder="e.g. Submit VAT return"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="date" required value={newEvent.event_date} onChange={e => setNewEvent({...newEvent, event_date: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={newEvent.event_type} onChange={e => setNewEvent({...newEvent, event_type: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 outline-none text-sm bg-white">
                    <option value="custom">Custom</option>
                    <option value="sars">SARS</option>
                    <option value="vat">VAT</option>
                    <option value="paye">PAYE</option>
                    <option value="payment">Payment</option>
                    <option value="deadline">Deadline</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea rows={2} value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})}
                    placeholder="Brief description..."
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 outline-none text-sm resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowAdd(false)}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit"
                    className="flex-1 py-2.5 bg-blue-700 text-white rounded-xl text-sm font-bold hover:bg-blue-800">
                    Add Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
